import {Box} from "@mui/system";
import {
  PolygonLayer,
  LayerEvent,
  MapScene,
  Control,
  Popup,
  LineLayer,
  ColorComponent,
} from "@antv/l7-react";
import React, {useEffect, useState} from "react";
import {usePapaParse} from "react-papaparse";
import DownloadButton from "../Common/DownloadButton";
import ModalTrigger from "../Common/ModalTrigger";
import MapTooltip from "./MapTooltip";

const CommodityMap = (props) => {
  const filter = props.filter;
  const buttonsFilter = props.buttonsFilter;
  const json = props.json;
  const csv = props.csv;
  const {readString} = usePapaParse();
  const [hovered, setHovered] = useState(undefined);
  const [chart, setChart] = useState(undefined);
  const [unknown, setUnknown] = useState(undefined);

  const initialState = {
    json: null,
    conv: null,
  };
  const [data, setData] = useState(initialState);

  const [mapEmptyState, setMapEmptyState] = useState(3);

  const handleReadString = () => {
    readString(csv, {
      complete: (results, file) => {
        json.features.map((j) => {
          let sum = 0;
          results.data
          .filter((d) => {
            return (
              d["Country of origin of the sample taken"] ===
              j.properties["NAME_ENGL"] && d["Food commodity"] === filter
            );
          })
          .forEach((f) => {
            sum += f[buttonsFilter];
          });
          j.properties = {...j.properties, sum};
          return j;
        });

        if (chart) {
          chart.layer.setData(json);
        }

        setData({json: json, conv: results.data});
        const len = results.data.filter(
          (d) =>
            d["Food commodity"] === filter &&
            (d["Above legal limit result"] !== 0 ||
              d["Result non compliant for pesticides"] !== 0)
        ).length;

        setMapEmptyState(len);

        let sumUnkown = 0;
        setUnknown(
          results.data
          .filter((d) => d["Food commodity"] === filter)
          .forEach((f) => {
            if (f["Region"] === "Unknown") {
              sumUnkown += f[buttonsFilter] ? f[buttonsFilter] : 0;
            }
          })
        );
        setUnknown(sumUnkown);
      },
      download: true,
      header: true,
      dynamicTyping: true,
      error: (error, file) => {
        console.log("Error while parsing:", error, file);
      },
    });
  };

  const handleLoaded = (scene, layer) => {
    setChart({scene, layer});
  };

  useEffect(() => {
    handleReadString();
  }, [filter, buttonsFilter]);

  const [canvas, setCanvas] = useState(undefined);
  let isMounted = true;

  const getTooltipContent = (country) => {
    const found = data.conv.find(
      (d) =>
        d["Country of origin of the sample taken"] === country &&
        d["Food commodity"] === filter
    );
    if (found) {
      return (
        <>
          { Object.keys(found).map((f) => {
            if (
              f === "Food commodity" ||
              f === "Country of origin of the sample taken"
            ) {
              return "";
            }
            return (
              <span
                style={ {display: "block", fontSize: "12px", color: "#22294D"} }
              >
                { " " }
                { f + ":" + found[f] }
              </span>
            );
          }) }
        </>
      );
    }
  };

  const getSemiEmptyState = () => {
    const metric =
      buttonsFilter === "Above legal limit result"
        ? "MRL exceedances"
        : "Non-compliant samples";

    const countries = data.conv
    .filter(
      (d) =>
        d["Food commodity"] === filter &&
        (d["Above legal limit result"] !== 0 ||
          d["Result non compliant for pesticides"] !== 0)
    )
    .map((f) => f["Country of origin of the sample taken"]);

    const countryText = countries.length
      ? `(${ countries.join(" and ") })`
      : "";

    return (
      metric +
      ` were related to products grown and/or potentially treated post-harvest in ${countries.length} ${countries.length === 1 ? 'country' : 'countries' } ` +
      countryText
    );
  };


  const colors = [
    "#fff",
    "#D0D1D5",
    "#BDBFC6",
    "#9699A7",
    "#6F7389",
    "#484D6A",
    "#22294D",
  ];

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 900);
    }

    window.addEventListener("resize", handleResize);
    return (_) => {
      window.removeEventListener("resize", handleResize);
    };
  });
  useEffect(() => {
    const canvases = document.getElementsByClassName("l7-marker-container");
    for (let i = 0; i < canvases.length; i++) {
      canvases[i].onwheel = function (event) {
        event.stopPropagation();
      };
    }
  });

  return (
    <Box>
      { mapEmptyState > 2 && data.json && (
        <Box
          display="flex"
          justifyContent="space-between"
          flexDirection={ {xs: "column", sm: "row"} }
        >
          <p id="commodityMapTitle">
            MRL exceedances and non‑compliant samples by country of origin
          </p>

          <Box display="flex" justifyContent="flex-end">
            <div
              className="legend-nodata"
              style={ {
                width: "20%",
                marginRight: "10px",
                marginTop: "1px",
              } }
            >
              <ColorComponent items={ [{title: "No Data", color: "#fff"}] }/>
            </div>
            <div
              className="legent-title"
              style={ {
                fontFamily: "Roboto Condensed, sans-serif",
                minWidth: isMobile ? "250px" : "300px",
              } }
            >
              <ColorComponent
                items={ [
                  {title: "1", color: "#D0D1D5"},
                  {title: "2", color: "#BDBFC6"},
                  {title: "3", color: "#9699A7"},
                  {title: "4", color: "#6F7389"},
                  {title: "5", color: "#484D6A"},
                  {title: "6+", color: "#22294D"},
                ] }
              />
            </div>
            <Box pl="10px">
              <DownloadButton
                isMap
                canvas={ canvas }
                fileName="Map"
                csv={ data.conv }
              />
            </Box>
            <Box pl="10px">
              <ModalTrigger
                style={ {color: "#22294D", cursor: "pointer"} }
                text="Kosovo Disclaimer"
              />
            </Box>
          </Box>
        </Box>
      ) }
      { mapEmptyState > 2 && data.json && (
        <MapScene
          onSceneLoaded={ (scene) => {
            setCanvas(scene);
          } }
          autoFit
          map={ {
            pitch: 0,
            style: "blank",
            zoom: 0,
            minZoom: 0,
            maxZoom: 4,
            center: [112, 30],
          } }
          style={ {
            height: "500px",
            position: "relative",
          } }
        >
          <PolygonLayer
            onLayerLoaded={ (layer, scene) => {
              handleLoaded(scene, layer);
            } }
            key={ 2 }
            zIndex={ 1 }
            source={ {
              data: data.json,
            } }
            color={ {
              field: "sum",
              values: (d) => {
                return d >= colors.length - 1
                  ? colors[colors.length - 1]
                  : colors[d];
              },
            } }
            options={ {
              autoFit: true,
            } }
            size={ {
              values: 1,
            } }
            shape={ {
              values: "fill",
            } }
            active={ {
              option: {
                color: "#C6C2C2",
              },
            } }
          >
            <LayerEvent
              type="mouseenter"
              handler={ (e) => {
                setHovered(e);
              } }
            />
            <LayerEvent
              type="mouseout"
              handler={ (e) => {
                setHovered(undefined);
              } }
            />
          </PolygonLayer>
          <PolygonLayer
            key={ 1 }
            source={ {
              data: data.json,
            } }
            size={ {
              values: 0.5,
            } }
            color={ {
              values: "#C6C2C2",
            } }
            shape={ {
              values: "line",
            } }
            style={ {
              opacity: 0.5,
            } }
            zIndex={ 10 }
          />
          <LineLayer
            key={ 5 }
            zIndex={ 25 }
            source={ {
              data: {
                type: 'FeatureCollection',
                features: data.json.features.filter(
                  (feature) => feature.properties.ISO3_CODE === 'XKX-SRB'
                )
              }
            } }
            size={ {values: 0.8} }
            color={ {values: 'white'} }
            shape={ {values: 'line'} }
            style={ {
              lineType: 'dash',
              dashArray: [2, 2],
              opacity: 0.9
            } }
          />
          <LineLayer
            key={ 5 }
            zIndex={ 25 }
            source={ {
              data: {
                type: 'FeatureCollection',
                features: data.json.features.filter(
                  (feature) => feature.properties.ISO3_CODE === 'PSE'
                )
              }
            } }
            size={ {values: 0.8} }
            color={ {values: 'white'} }
            shape={ {values: 'line'} }
            style={ {
              lineType: 'dash',
              dashArray: [2, 2],
              opacity: 0.9
            } }
          />
          { hovered &&
          data.conv.find(
            (d) =>
              d["Country of origin of the sample taken"] ===
              hovered.feature.properties.NAME_ENGL &&
              d["Food commodity"] === filter
          ) && (
            <MapTooltip>
              <div className="tooltipWrapper">
                {
                  <b
                    style={ {
                      color: "#22294D",
                      fontFamily: "Roboto Condensed",
                      fontSize: "16px",
                      paddingBottom: "5px",
                    } }
                  >
                    { hovered.feature.properties.NAME_ENGL }
                  </b>
                }
                <br/>
                { getTooltipContent(hovered.feature.properties.NAME_ENGL) }
              </div>
            </MapTooltip>
          ) }
          <Control type="scale" position="bottomright"/>
          <Control type="zoom" position="bottomright"/>
        </MapScene>
      ) }
      { unknown > 0 && (
        <div className="unknown-legend-wrapper" style={ {fontFamily: "Roboto Condensed, sans-serif", width: "100px"} }>
          <ColorComponent items={ [{title: "Unknown: " + unknown, color: "#9D3140"}] }/>
        </div>
      ) }
      { mapEmptyState === 0 && (
        <Box display="flex" justifyContent="center">
          <p
            style={ {
              color: "#22294D",
              fontWeight: "700",
              maxWidth: "280px",
              textAlign: "center",
              fontSize: "14px",
              backgroundColor: "white",
              border: "1px solid #22294D",
              borderRadius: "10px",
              padding: "10px 10px 10px 10px",
            } }
          >
            No{ " " }
            { buttonsFilter === "Above legal limit result"
              ? "MRL exceedances"
              : "non-compliant samples" }{ " " }
            were reported to this food product.
          </p>
        </Box>
      ) }

      { mapEmptyState > 0 && mapEmptyState < 3 && (
        <Box display="flex" justifyContent="center">
          <p
            style={ {
              color: "#22294D",
              fontWeight: "700",
              maxWidth: "350px",
              textAlign: "center",
              fontSize: "14px",
              backgroundColor: "white",
              borderRadius: "10px",
              border: "1px solid #22294D",
              padding: "10px 10px 10px 10px",
            } }
          >
            { getSemiEmptyState() }
          </p>
        </Box>
      ) }
    </Box>
  );
};

export default CommodityMap;
